import React, { useState, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Clock, Check, X, Zap, XCircle, DollarSign, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChatButton } from '@/components/dashboard/shared/ChatButton';
import { RefundManagementModal } from '@/components/dashboard/practitioner/RefundManagementModal';
import { useNavigate } from 'react-router-dom';
import { getFullName } from '@/utils/name-helpers';
import { normalizeSessionFormat, getSessionFormatDisplayName } from '@/utils/session-format-helpers';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '@/contexts/AuthContext';
import { ClientNotesModal } from '../ClientNotesModal';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  payment_status: string;
  client_notes?: string;
  practitioner_notes?: string;
  refund_status?: string;
  refund_reason?: string;
  stripe_payment_intent_id?: string | null;
  client_id: string;
  confirmation_deadline?: string | null;
  service: {
    service_name: string;
  };
  clients: {
    first_name: string | null;
    last_name: string | null;
  };
}

interface TimeChangeRequest {
  id: string;
  client_reason: string;
  requested_start_time: string;
  requested_end_time: string;
  original_start_time: string;
  original_end_time: string;
  status: string;
  practitioner_response?: string;
  created_at: string;
}

interface PractitionerAppointmentTableRowProps {
  appointment: Appointment;
  onNotesClick?: (notes: string) => void;
  onRefresh?: () => void;
  unreadCount?: number;
  practitionerTimezone?: string;
  onRefreshUnreadCounts?: () => void;
}

export const PractitionerAppointmentTableRow: React.FC<PractitionerAppointmentTableRowProps> = ({
  appointment,
  onNotesClick,
  onRefresh,
  unreadCount = 0,
  practitionerTimezone = 'America/New_York',
  onRefreshUnreadCounts
}) => {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const [timeChangeRequest, setTimeChangeRequest] = useState<TimeChangeRequest | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isClientNotesOpen, setIsClientNotesOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [googleMeetLink, setGoogleMeetLink] = useState<string | null>(null);

  // Fetch time change request for this appointment
  useEffect(() => {
    const fetchTimeChangeRequest = async () => {
      const { data, error } = await supabase
        .from('booking_time_change_requests')
        .select('*')
        .eq('booking_id', appointment.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (!error && data) {
        setTimeChangeRequest(data);
      }
    };

    fetchTimeChangeRequest();
  }, [appointment.id]);

  // Fetch Google Meet link for this appointment
  useEffect(() => {
    const fetchGoogleMeetLink = async () => {
      console.log('🔍 Fetching Google Meet link for booking:', appointment.id);

      const { data, error } = await supabase
        .from('google_calendar_meetings')
        .select('meeting_link')
        .eq('booking_id', appointment.id)
        .maybeSingle();

      console.log('📊 Google Meet query result:', { data, error, bookingId: appointment.id });

      if (error) {
        console.error('❌ Error fetching Google Meet link:', error);
        return;
      }

      if (data?.meeting_link) {
        console.log('✅ Found Google Meet link:', data.meeting_link);
        setGoogleMeetLink(data.meeting_link);
      } else {
        console.log('ℹ️ No Google Meet link found for this booking');
        setGoogleMeetLink(null);
      }
    };

    fetchGoogleMeetLink();
  }, [appointment.id]);

  const handleTimeChangeResponse = async (action: 'approved' | 'rejected') => {
    if (!timeChangeRequest) return;
    
    setIsProcessing(true);
    
    try {
      const { error } = await supabase
        .from('booking_time_change_requests')
        .update({
          status: action,
          practitioner_responded_at: new Date().toISOString(),
        })
        .eq('id', timeChangeRequest.id);

      if (error) {
        console.error('Error updating time change request:', error);
        toast({
          title: t('timeChangeRequests.list.operationFailed'),
          description: t('timeChangeRequests.list.updateError'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: action === 'approved' ? t('timeChangeRequests.list.approved') : t('timeChangeRequests.list.rejected'),
        description: action === 'approved' 
          ? t('timeChangeRequests.list.approvedDesc')
          : t('timeChangeRequests.list.rejectedDesc'),
      });

      setTimeChangeRequest(null);
      onRefresh?.();
    } catch (error) {
      console.error('Error processing time change request:', error);
      toast({
        title: t('timeChangeRequests.list.operationFailed'),
        description: t('timeChangeRequests.list.networkError'),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancellationReason.trim()) {
      toast({
        title: "Cancellation reason required",
        description: "Please provide a reason for cancelling this appointment.",
        variant: "destructive",
      });
      return;
    }

    setIsCancelling(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('cancel-booking-by-practitioner', {
        body: {
          bookingId: appointment.id,
          reason: cancellationReason
        }
      });

      if (error) {
        console.error('Error cancelling booking:', error);
        toast({
          title: "Cancellation Failed",
          description: "Failed to cancel the appointment. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Appointment Cancelled",
        description: data.refundInitiated 
          ? "The appointment has been cancelled and a refund has been initiated." 
          : "The appointment has been cancelled. The client has been notified.",
      });

      setCancellationReason('');
      onRefresh?.();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast({
        title: "Cancellation Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const formatDateTime = (dateString: string, timezone: string) => {
    const date = new Date(dateString);
    
    // Get current language from i18n
    const currentLanguage = t('common:language') === 'zh' ? 'zh-CN' : 'en-US';
    const dateFormat = currentLanguage === 'zh-CN' ? 'yyyy年MM月dd日' : 'MMM dd, yyyy';
    
    return {
      date: formatInTimeZone(date, timezone, dateFormat),
      time: formatInTimeZone(date, timezone, 'HH:mm')
    };
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      'Reserved': 'outline',
      'Scheduled': 'default',
      'Completed': 'secondary',
      'Cancelled': 'destructive',
      'Refunded': 'outline'
    };
    
    // Get translated status text
    const statusMap: Record<string, string> = {
      'Reserved': t('appointments.status.reserved'),
      'Scheduled': t('appointments.status.scheduled'),
      'Completed': t('appointments.status.completed'),
      'Cancelled': t('appointments.status.cancelled'),
      'Refunded': t('appointments.status.refunded')
    };
    
    const displayStatus = statusMap[status] || status;
    
    // Special styling for Reserved status
    const className = status === 'Reserved' ? 'bg-orange-100 text-orange-800 border-orange-300' : '';
    
    return <Badge variant={variants[status] || 'outline'} className={className}>{displayStatus}</Badge>;
  };

  const getFormatDisplay = (format: string) => {
    // Use the standardized session format helper
    return getSessionFormatDisplayName(format);
  };

  const startDateTime = formatDateTime(appointment.start_time, practitionerTimezone);
  const clientName = (() => {
    const first = appointment.clients?.first_name?.trim() || '';
    const last = appointment.clients?.last_name?.trim() || '';
    if (first || last) return `${first} ${last}`.trim();
    return 'Client';
  })();
  
  // Get current language for date formatting
  const currentLanguage = t('common:language') === 'zh' ? 'zh-CN' : 'en-US';
  
  // All appointments should show basic action buttons - remove restrictions
  const canChat = true; // Always available for communication
  
  // Tools available for all appointments
  const canRecord = true;
  
  // Refund button available only for completed paid bookings (not upcoming/scheduled)
  const canRefund = appointment.status === 'Completed' &&
                   appointment.stripe_payment_intent_id !== null && 
                   appointment.stripe_payment_intent_id !== '' &&
                   !appointment.refund_status;
  
  // Check if appointment can be cancelled (only upcoming scheduled appointments)
  const canCancel = appointment.status === 'Scheduled' && new Date(appointment.start_time) > new Date();

  return (
    <>
      <TableRow className={timeChangeRequest ? 'bg-yellow-50' : ''}>
        <TableCell>{startDateTime.date}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            {startDateTime.time}
            {timeChangeRequest && (
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                <Clock className="h-3 w-3 mr-1" />
                {t('timeChangeRequests.status.pending')}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>{clientName}</TableCell>
        <TableCell>{appointment.service.service_name}</TableCell>
        <TableCell>{getStatusBadge(appointment.status)}</TableCell>
        <TableCell>{getFormatDisplay(appointment.format)}</TableCell>
        <TableCell>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex gap-1 lg:gap-1 lg:flex-wrap max-w-[320px] lg:max-w-none">
            {/* Chat Button - Always available */}
            <ChatButton
              bookingId={appointment.id}
              className="flex items-center justify-center gap-1 text-green-600 border-green-200 hover:bg-green-50 min-h-[36px] text-xs sm:text-sm"
              currentUserType="practitioner"
              otherParticipantName={clientName || 'Client'}
              unreadCount={unreadCount}
              onRefreshUnreadCounts={onRefreshUnreadCounts}
            />

            {/* Google Meet Link Button - Show if link exists and appointment is upcoming/scheduled */}
            {googleMeetLink && (appointment.status === 'Scheduled' || appointment.status === 'Reserved') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(googleMeetLink, '_blank')}
                className="flex items-center justify-center gap-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50 min-h-[36px] text-xs sm:text-sm"
              >
                <Video className="h-4 w-4" />
                <span>Meet</span>
              </Button>
            )}

            {/* Client Notes Button - Always available */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsClientNotesOpen(true)}
              className="flex items-center justify-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50 min-h-[36px] text-xs sm:text-sm"
            >
              <FileText className="h-4 w-4" />
              <span>Notes</span>
            </Button>

            {/* Tools button for completed/scheduled appointments */}
            {canRecord && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/session-tools/${appointment.id}`)}
                className="flex items-center justify-center gap-1 text-purple-600 border-purple-200 hover:bg-purple-50 min-h-[36px] text-xs sm:text-sm"
              >
                <Zap className="h-4 w-4" />
                <span>Tools</span>
              </Button>
            )}

            {/* Refund Button - Show for completed paid bookings only */}
            {canRefund && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRefundModalOpen(true)}
                className="flex items-center justify-center gap-1 text-orange-600 border-orange-200 hover:bg-orange-50 min-h-[36px] text-xs sm:text-sm"
              >
                <DollarSign className="h-4 w-4" />
                <span>Refund</span>
              </Button>
            )}

            {/* Cancel Button */}
            {canCancel && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center justify-center gap-1 text-red-600 border-red-200 hover:bg-red-50 min-h-[36px] text-xs sm:text-sm"
                    >
                      <XCircle className="h-4 w-4" />
                      <span>Cancel</span>
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-[95vw] sm:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-base sm:text-lg">Cancel Appointment</AlertDialogTitle>
                    <AlertDialogDescription className="text-sm">
                      Are you sure you want to cancel this appointment? The client will be notified and if this is a paid session, a refund will be automatically processed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="my-4">
                    <label className="text-sm font-medium mb-2 block">
                      Reason for cancellation (required):
                    </label>
                    <Textarea
                      placeholder="Please provide a reason for cancelling this appointment..."
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      className="min-h-[80px] text-sm"
                    />
                  </div>
                  <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                    <AlertDialogCancel className="w-full sm:w-auto">Keep Appointment</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelBooking}
                      disabled={isCancelling || !cancellationReason.trim()}
                      className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel Appointment'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

          </div>
        </TableCell>
      </TableRow>

      {/* Time Change Request Details Row */}
      {timeChangeRequest && (
        <TableRow className="bg-yellow-50">
          <TableCell colSpan={7} className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {t('timeChangeRequests.list.title')}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">{timeChangeRequest.client_reason}</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-red-600">{t('timeChangeRequests.list.originalTime')}: </span>
                      <span>
                        {currentLanguage === 'zh-CN' 
                          ? `${format(new Date(timeChangeRequest.original_start_time), 'MM月dd日 HH:mm')} - ${format(new Date(timeChangeRequest.original_end_time), 'HH:mm')}`
                          : `${format(new Date(timeChangeRequest.original_start_time), 'MM/dd HH:mm')} - ${format(new Date(timeChangeRequest.original_end_time), 'HH:mm')}`
                        }
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-green-600">{t('timeChangeRequests.list.newTime')}: </span>
                      <span>
                        {currentLanguage === 'zh-CN' 
                          ? `${format(new Date(timeChangeRequest.requested_start_time), 'MM月dd日 HH:mm')} - ${format(new Date(timeChangeRequest.requested_end_time), 'HH:mm')}`
                          : `${format(new Date(timeChangeRequest.requested_start_time), 'MM/dd HH:mm')} - ${format(new Date(timeChangeRequest.requested_end_time), 'HH:mm')}`
                        }
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTimeChangeResponse('rejected')}
                    disabled={isProcessing}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('timeChangeRequests.list.reject')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleTimeChangeResponse('approved')}
                    disabled={isProcessing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    {t('timeChangeRequests.list.approve')}
                  </Button>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}

      {/* Refund Management Modal */}
      <RefundManagementModal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        bookingId={appointment.id}
        onStatusUpdate={onRefresh || (() => {})}
      />

      {/* Client Notes Modal */}
      <ClientNotesModal
        isOpen={isClientNotesOpen}
        onClose={() => setIsClientNotesOpen(false)}
        bookingId={appointment.id}
        clientName={clientName}
      />

    </>
  );
};